/**
 * CostEntryForm — modal for attributing a new cost to a quality event.
 *
 * Tier 1.1 scope: minimal viable form with category picker + a single
 * labor line + a free-form "other" amount. Multi-line itemization
 * (multiple LABOR + MATERIALS + EXTERNAL lines) is Tier 1.2.
 *
 * POSTs to /api/v1/finance/cost-entries which writes the entry +
 * audit-log row (FINANCE_COST_ATTRIBUTED). Returns the saved entry to
 * the parent so it can refetch the list.
 */

import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@edms/design-system';
import { apiFetch } from '../../../api/client';
import { useFocusTrap } from '../../../hooks/useFocusTrap';
import styles from './CostEntryForm.module.css';

interface CostCategory {
  category_id: string;
  display_name: string;
  paf_bucket: string;
  description: string | null;
}

interface CostRate {
  rate_id: string;
  role_code: string;
  role_display_name: string;
  hourly_rate_minor: number | string;
  currency_code: string;
}

interface CostEntryFormProps {
  subjectEntityType: string;
  subjectEntityId: string;
  subjectDisplayLabel?: string;
  onClose: () => void;
  onSubmitted: () => void;
}

export function CostEntryForm({
  subjectEntityType,
  subjectEntityId,
  subjectDisplayLabel,
  onClose,
  onSubmitted,
}: CostEntryFormProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, onClose);

  // Local form state
  const [categoryId, setCategoryId] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [occurredAt, setOccurredAt] = useState<string>(
    () => new Date().toISOString().slice(0, 10),
  );
  const [laborRoleCode, setLaborRoleCode] = useState<string>('');
  const [laborHours, setLaborHours] = useState<string>('');
  const [otherCostUsd, setOtherCostUsd] = useState<string>('');
  const [otherCostDesc, setOtherCostDesc] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load category + rate dictionaries
  const categoriesQ = useQuery<{ items: CostCategory[] }>({
    queryKey: ['finance', 'cost-categories'],
    queryFn: ({ signal }) =>
      apiFetch<{ items: CostCategory[] }>(`/api/v1/finance/cost-categories`, { signal }),
    staleTime: 5 * 60 * 1000,
  });
  const ratesQ = useQuery<{ items: CostRate[] }>({
    queryKey: ['finance', 'cost-rates'],
    queryFn: ({ signal }) =>
      apiFetch<{ items: CostRate[] }>(`/api/v1/finance/cost-rates`, { signal }),
    staleTime: 5 * 60 * 1000,
  });

  // Compute the labor line total client-side (server recomputes via trigger)
  const labor = useMemo(() => {
    const hours = parseFloat(laborHours);
    if (!laborRoleCode || !hours || isNaN(hours) || hours <= 0) return null;
    const rate = ratesQ.data?.items.find((r) => r.role_code === laborRoleCode);
    if (!rate) return null;
    const hourlyMinor = Number(rate.hourly_rate_minor);
    const lineTotal = Math.round(hours * hourlyMinor);
    return {
      role_code: laborRoleCode,
      hours_worked: hours,
      hourly_rate_minor: hourlyMinor,
      line_total_minor: lineTotal,
      role_display: rate.role_display_name,
    };
  }, [laborRoleCode, laborHours, ratesQ.data]);

  // Compute the "other" line
  const other = useMemo(() => {
    const usd = parseFloat(otherCostUsd);
    if (!usd || isNaN(usd) || usd <= 0) return null;
    return {
      line_type: 'OTHER' as const,
      description: otherCostDesc || 'Other costs',
      line_total_minor: Math.round(usd * 100),
    };
  }, [otherCostUsd, otherCostDesc]);

  const grandTotalMinor = (labor?.line_total_minor || 0) + (other?.line_total_minor || 0);

  function formatMinor(minor: number): string {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
    }).format(minor / 100);
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!categoryId) {
      setError(t('finance.costEntryForm.errCategory', { defaultValue: 'Pick a cost category.' }) as string);
      return;
    }
    if (!labor && !other) {
      setError(t('finance.costEntryForm.errNoLines', {
        defaultValue: 'Add at least one line (labor or other costs).',
      }) as string);
      return;
    }
    const lines: Array<Record<string, unknown>> = [];
    if (labor) {
      lines.push({
        line_type: 'LABOR',
        role_code: labor.role_code,
        hours_worked: labor.hours_worked,
        hourly_rate_minor: labor.hourly_rate_minor,
        line_total_minor: labor.line_total_minor,
        description: `${labor.role_display} — ${labor.hours_worked}h`,
      });
    }
    if (other) {
      lines.push(other);
    }
    setSubmitting(true);
    try {
      await apiFetch(`/api/v1/finance/cost-entries`, {
        method: 'POST',
        body: {
          subject_entity_type: subjectEntityType,
          subject_entity_id: subjectEntityId,
          subject_display_label: subjectDisplayLabel,
          category_id: categoryId,
          currency_code: 'USD',
          occurred_at_utc: new Date(occurredAt + 'T12:00:00Z').toISOString(),
          description: description || null,
          lines,
        },
      });
      onSubmitted();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  }

  // Group categories by PAF bucket for cleaner picker
  const groupedCategories = useMemo(() => {
    const map = new Map<string, CostCategory[]>();
    for (const c of categoriesQ.data?.items ?? []) {
      if (!map.has(c.paf_bucket)) map.set(c.paf_bucket, []);
      map.get(c.paf_bucket)!.push(c);
    }
    return map;
  }, [categoriesQ.data]);

  return (
    <div ref={dialogRef} className={styles.backdrop} role="dialog" aria-modal="true" aria-labelledby="cef-title">
      <div className={styles.modal}>
        <header className={styles.header}>
          <h2 id="cef-title" className={styles.title}>
            {t('finance.costEntryForm.title', { defaultValue: 'Attribute cost' })}
          </h2>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <p className={styles.subjectLabel}>
          {subjectDisplayLabel || `${subjectEntityType} ${subjectEntityId.slice(0, 8)}…`}
        </p>

        <form onSubmit={onSubmit} className={styles.form}>
          {/* Category */}
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              {t('finance.costEntryForm.category', { defaultValue: 'Cost category' })} *
            </span>
            <select
              className={styles.select}
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              required
            >
              <option value="">— select —</option>
              {Array.from(groupedCategories.entries()).map(([bucket, cats]) => (
                <optgroup key={bucket} label={bucket.replace(/_/g, ' ')}>
                  {cats.map((c) => (
                    <option key={c.category_id} value={c.category_id}>
                      {c.display_name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>

          {/* Occurred at */}
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              {t('finance.costEntryForm.occurredAt', { defaultValue: 'When did it occur' })} *
            </span>
            <input
              type="date"
              className={styles.input}
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              required
              max={new Date().toISOString().slice(0, 10)}
            />
          </label>

          {/* Description */}
          <label className={styles.field}>
            <span className={styles.fieldLabel}>
              {t('finance.costEntryForm.description', { defaultValue: 'Description (optional)' })}
            </span>
            <input
              type="text"
              className={styles.input}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('qms.finance.costEntryForm.placeholders.description', { defaultValue: 'e.g. RCA investigation + lab work' })}
              maxLength={500}
            />
          </label>

          {/* Labor line */}
          <fieldset className={styles.lineGroup}>
            <legend>
              {t('finance.costEntryForm.labor', { defaultValue: 'Labor (optional)' })}
            </legend>
            <div className={styles.row}>
              <label className={styles.fieldFlex}>
                <span className={styles.fieldLabel}>{t('qms.finance.costEntryForm.fields.role', { defaultValue: 'Role' })}</span>
                <select
                  className={styles.select}
                  value={laborRoleCode}
                  onChange={(e) => setLaborRoleCode(e.target.value)}
                >
                  <option value="">{t('qms.finance.costEntryForm.placeholders.pickRole', { defaultValue: '— pick role —' })}</option>
                  {ratesQ.data?.items.map((r) => (
                    <option key={r.rate_id} value={r.role_code}>
                      {r.role_display_name} — ${(Number(r.hourly_rate_minor) / 100).toFixed(2)}/h
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.fieldNarrow}>
                <span className={styles.fieldLabel}>{t('qms.finance.costEntryForm.fields.hours', { defaultValue: 'Hours' })}</span>
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  className={styles.input}
                  value={laborHours}
                  onChange={(e) => setLaborHours(e.target.value)}
                  placeholder={t('qms.finance.costEntryForm.placeholders.hours', { defaultValue: '0' })}
                />
              </label>
            </div>
            {labor && (
              <p className={styles.linePreview}>
                {labor.hours_worked}h × ${(labor.hourly_rate_minor / 100).toFixed(2)}/h ={' '}
                <strong>{formatMinor(labor.line_total_minor)}</strong>
              </p>
            )}
          </fieldset>

          {/* Other costs line */}
          <fieldset className={styles.lineGroup}>
            <legend>
              {t('finance.costEntryForm.other', { defaultValue: 'Other costs (materials / external) — optional' })}
            </legend>
            <div className={styles.row}>
              <label className={styles.fieldFlex}>
                <span className={styles.fieldLabel}>{t('qms.finance.costEntryForm.fields.description', { defaultValue: 'Description' })}</span>
                <input
                  type="text"
                  className={styles.input}
                  value={otherCostDesc}
                  onChange={(e) => setOtherCostDesc(e.target.value)}
                  placeholder={t('qms.finance.costEntryForm.placeholders.otherDescription', { defaultValue: 'e.g. External lab fees' })}
                />
              </label>
              <label className={styles.fieldNarrow}>
                <span className={styles.fieldLabel}>{t('qms.finance.costEntryForm.fields.currency', { defaultValue: 'USD' })}</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={styles.input}
                  value={otherCostUsd}
                  onChange={(e) => setOtherCostUsd(e.target.value)}
                  placeholder={t('qms.finance.costEntryForm.placeholders.otherAmount', { defaultValue: '0.00' })}
                />
              </label>
            </div>
          </fieldset>

          {/* Grand total */}
          <div className={styles.grandTotal}>
            <span>{t('qms.finance.costEntryForm.fields.total', { defaultValue: 'Total' })}</span>
            <strong>{formatMinor(grandTotalMinor)}</strong>
          </div>

          {error && (
            <div className={styles.errorBox} role="alert">{error}</div>
          )}

          <div className={styles.actions}>
            <Button variant="tertiary" size="md" type="button" onClick={onClose}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button variant="primary" size="md" type="submit" isLoading={submitting}>
              {t('finance.costEntryForm.save', { defaultValue: 'Save cost entry' })}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
